var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { createSlice, current } from "@reduxjs/toolkit";
import { isUndefined, EInvalidVotePolicy, } from "@sequentech/ui-core";
import { computeSlateChoices } from "../../services/SlateChoices";
import { computeClearChoices } from "../../services/ClearSelections";
import { countSelections, exceedsMaximum, isSelected, refusesOverVotes, replacesOnSelect, } from "../../services/SelectionLimits";
var initialState = {};
/**
 * The codec rejects a ballot-level blank flag that disagrees with a
 * contest's actual content (a real selection, an invalid mark, or a
 * decline), so any reducer that makes such a change must clear
 * is_blank_ballot across every contest in the election, not just the
 * one being edited.
 */
var clearBlankBallotFlag = function (election) {
    election.forEach(function (contest) {
        contest.is_blank_ballot = false;
    });
};
export var ballotSelectionsSlice = createSlice({
    name: "ballotSelections",
    initialState: initialState,
    reducers: {
        clearBallot: function (state) {
            state = initialState;
            return initialState;
        },
        setBallotSelection: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (currentElection) {
                state[action.payload.ballotStyle.election_id] = action.payload.ballotSelection;
            }
            return state;
        },
        resetBallotSelection: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (!currentElection || action.payload.force) {
                state[action.payload.ballotStyle.election_id] =
                    action.payload.ballotStyle.ballot_eml.contests.map(function (question) {
                        var _a;
                        var currentContestValue = (_a = state[action.payload.ballotStyle.election_id]) === null || _a === void 0 ? void 0 : _a.find(function (contest) { return contest.contest_id === question.id; });
                        if (currentContestValue &&
                            action.payload.contestId &&
                            action.payload.contestId !== question.id) {
                            return {
                                contest_id: currentContestValue.contest_id,
                                is_explicit_invalid: currentContestValue.is_explicit_invalid,
                                is_decline_to_vote: currentContestValue.is_decline_to_vote,
                                is_blank_ballot: currentContestValue.is_blank_ballot,
                                invalid_errors: currentContestValue.invalid_errors,
                                invalid_alerts: currentContestValue.invalid_alerts,
                                choices: currentContestValue.choices,
                            };
                        }
                        return {
                            contest_id: question.id,
                            is_explicit_invalid: false,
                            is_decline_to_vote: false,
                            is_blank_ballot: false,
                            invalid_errors: [],
                            invalid_alerts: [],
                            choices: question.candidates.map(function (answer) { return ({
                                id: answer.id,
                                selected: -1,
                            }); }),
                        };
                    });
                // A single reset contest (force + contestId) is rebuilt as
                // not blank while every other contest keeps its prior
                // is_blank_ballot value, which can leave the election with
                // some contests blank and one not -- a combination the
                // codec rejects.
                var resetElection = state[action.payload.ballotStyle.election_id];
                if (resetElection) {
                    clearBlankBallotFlag(resetElection);
                }
            }
            return state;
        },
        setBallotSelectionInvalidVote: function (state, action) {
            var _a, _b;
            var ballotEmlContest = action.payload.ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === action.payload.contestId; });
            // check bounds
            if (isUndefined(ballotEmlContest)) {
                return state;
            }
            // find question
            var currentElection = state[action.payload.ballotStyle.election_id];
            var currentQuestion = currentElection === null || currentElection === void 0 ? void 0 : currentElection.find(function (contest) { return contest.contest_id === action.payload.contestId; });
            // update state
            if (!isUndefined(currentQuestion)) {
                var isExclusive = ((_a = ballotEmlContest.presentation) === null || _a === void 0 ? void 0 : _a.invalid_vote_policy) ===
                    EInvalidVotePolicy.ALLOWED_WITH_EXCLUSIVE_EXPLICIT;
                if (action.payload.isExplicitInvalid &&
                    !currentQuestion.is_explicit_invalid &&
                    !isExclusive &&
                    refusesOverVotes(ballotEmlContest) &&
                    exceedsMaximum(ballotEmlContest, countSelections(ballotEmlContest, currentQuestion) + 1)) {
                    return state;
                }
                currentQuestion.is_explicit_invalid = action.payload.isExplicitInvalid;
                // Under ALLOWED_WITH_EXCLUSIVE_EXPLICIT, marking the ballot
                // explicit-invalid is mutually exclusive with any other
                // selection in the contest, mirroring how blank vote already
                // clears everything else when selected.
                if (action.payload.isExplicitInvalid &&
                    ((_b = ballotEmlContest.presentation) === null || _b === void 0 ? void 0 : _b.invalid_vote_policy) ===
                        EInvalidVotePolicy.ALLOWED_WITH_EXCLUSIVE_EXPLICIT) {
                    currentQuestion.choices = currentQuestion.choices.map(function (choice) { return (__assign(__assign({}, choice), { selected: -1 })); });
                }
                if (!isUndefined(currentElection)) {
                    clearBlankBallotFlag(currentElection);
                }
            }
            return state;
        },
        setBallotSelectionBlankVote: function (state, action) {
            var ballotEmlContest = action.payload.ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === action.payload.contestId; });
            // check bounds
            if (isUndefined(ballotEmlContest)) {
                return state;
            }
            // find question
            var currentElection = state[action.payload.ballotStyle.election_id];
            var currentQuestion = currentElection === null || currentElection === void 0 ? void 0 : currentElection.find(function (contest) { return contest.contest_id === action.payload.contestId; });
            // update state
            if (!isUndefined(currentQuestion)) {
                currentQuestion.is_explicit_invalid = false;
                currentQuestion.choices = currentQuestion.choices.map(function (choice) {
                    return __assign(__assign({}, choice), { selected: choice.id === action.payload.candidateId ? 0 : -1 });
                });
                if (!isUndefined(currentElection)) {
                    clearBlankBallotFlag(currentElection);
                }
            }
            return state;
        },
        setBallotSelectionVoteChoice: function (state, action) {
            var _a;
            var ballotEmlContest = action.payload.ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === action.payload.contestId; });
            // check bounds
            if (isUndefined(ballotEmlContest)) {
                return state;
            }
            var currentElection = state[action.payload.ballotStyle.election_id];
            var currentQuestion = currentElection === null || currentElection === void 0 ? void 0 : currentElection.find(function (contest) { return contest.contest_id === action.payload.contestId; });
            var currentChoiceIndex = currentQuestion === null || currentQuestion === void 0 ? void 0 : currentQuestion.choices.findIndex(function (choice) { return action.payload.voteChoice.id === choice.id; });
            var currentChoice = !isUndefined(currentElection) &&
                !isUndefined(currentChoiceIndex) &&
                currentChoiceIndex > -1
                ? currentQuestion === null || currentQuestion === void 0 ? void 0 : currentQuestion.choices[currentChoiceIndex]
                : undefined;
            // check election state
            if (!currentElection || isUndefined(currentChoice)) {
                return state;
            }
            // modify
            if (currentQuestion && !isUndefined(currentChoiceIndex)) {
                var explicitBlankCandidateIds_1 = new Set(ballotEmlContest.candidates
                    .filter(function (candidate) { var _a; return (_a = candidate.presentation) === null || _a === void 0 ? void 0 : _a.is_explicit_blank; })
                    .map(function (candidate) { return candidate.id; }));
                var isSelectingExplicitBlank = explicitBlankCandidateIds_1.has(action.payload.voteChoice.id) &&
                    action.payload.voteChoice.selected > -1;
                var isNewSelection = isSelected(action.payload.voteChoice) &&
                    !isSelected(currentChoice) &&
                    !isSelectingExplicitBlank;
                // The same limits apply whatever dispatches the choice, so
                // they are kept here and not only in the candidate controls.
                if (isNewSelection && replacesOnSelect(ballotEmlContest)) {
                    currentQuestion.is_explicit_invalid = false;
                    currentQuestion.choices = currentQuestion.choices.map(function (choice) { return (__assign(__assign({}, choice), { selected: -1 })); });
                }
                else if (isNewSelection &&
                    refusesOverVotes(ballotEmlContest) &&
                    exceedsMaximum(ballotEmlContest, countSelections(ballotEmlContest, currentQuestion) + 1)) {
                    return state;
                }
                currentQuestion.choices[currentChoiceIndex] = action.payload.voteChoice;
                if (action.payload.voteChoice.selected > -1 && !isSelectingExplicitBlank) {
                    currentQuestion.choices = currentQuestion.choices.map(function (choice) {
                        return explicitBlankCandidateIds_1.has(choice.id)
                            ? __assign(__assign({}, choice), { selected: -1 }) : choice;
                    });
                    // Under ALLOWED_WITH_EXCLUSIVE_EXPLICIT, selecting a real
                    // candidate is mutually exclusive with explicit invalid.
                    if (((_a = ballotEmlContest.presentation) === null || _a === void 0 ? void 0 : _a.invalid_vote_policy) ===
                        EInvalidVotePolicy.ALLOWED_WITH_EXCLUSIVE_EXPLICIT) {
                        currentQuestion.is_explicit_invalid = false;
                    }
                }
                clearBlankBallotFlag(currentElection);
            }
            return state;
        },
        applySlateSelection: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (isUndefined(currentElection)) {
                return state;
            }
            try {
                state[action.payload.ballotStyle.election_id] = computeSlateChoices(action.payload.slate, action.payload.ballotStyle.ballot_eml.contests, current(currentElection)).selection;
            }
            catch (error) {
                console.log("Error applying slate: ".concat(error));
            }
            return state;
        },
        clearBallotSelectionChoices: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (isUndefined(currentElection)) {
                return state;
            }
            state[action.payload.ballotStyle.election_id] = computeClearChoices(action.payload.ballotStyle.ballot_eml.contests, current(currentElection)).selection;
            return state;
        },
        setAllBallotSelectionsDeclineToVote: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (!isUndefined(currentElection)) {
                currentElection.forEach(function (currentQuestion) {
                    currentQuestion.is_decline_to_vote = true;
                    // A declined ballot must not carry per-contest explicit
                    // invalid markers, otherwise it would be tallied as
                    // invalid instead of declined.
                    currentQuestion.is_explicit_invalid = false;
                    // A ballot cannot be both declined and blank.
                    currentQuestion.is_blank_ballot = false;
                    currentQuestion.choices = currentQuestion.choices.map(function (choice) {
                        if (choice.selected > -1) {
                            choice.selected = -1;
                        }
                        return choice;
                    });
                });
            }
            return state;
        },
        setAllBallotSelectionsBlankBallot: function (state, action) {
            var currentElection = state[action.payload.ballotStyle.election_id];
            if (!isUndefined(currentElection)) {
                currentElection.forEach(function (currentQuestion) {
                    currentQuestion.is_blank_ballot = true;
                    // A ballot cannot be both blank and declined.
                    currentQuestion.is_decline_to_vote = false;
                });
            }
            return state;
        },
    },
    /*extraReducers: (builder) => {
        builder.addCase(fetchElectionByIdAsync.fulfilled, (state, action) => {
            if (!action.payload) {
                return state
            }
            ballotSelectionsSlice.caseReducers.resetBallotSelection(state, {
                payload: {
                    election: action.payload,
                },
                type: "ballotSelections/resetBallotSelection",
            })
            return state
        })
    },*/
});
export var clearBallot = (_a = ballotSelectionsSlice.actions, _a.clearBallot), setBallotSelection = _a.setBallotSelection, resetBallotSelection = _a.resetBallotSelection, setBallotSelectionInvalidVote = _a.setBallotSelectionInvalidVote, setBallotSelectionBlankVote = _a.setBallotSelectionBlankVote, setBallotSelectionVoteChoice = _a.setBallotSelectionVoteChoice, applySlateSelection = _a.applySlateSelection, clearBallotSelectionChoices = _a.clearBallotSelectionChoices, setAllBallotSelectionsDeclineToVote = _a.setAllBallotSelectionsDeclineToVote, setAllBallotSelectionsBlankBallot = _a.setAllBallotSelectionsBlankBallot;
export var selectBallotSelectionVoteChoice = function (electionId, contestId, answerIndex) { return function (state) {
    var _a, _b;
    return (_b = (_a = state.ballotSelections[electionId]) === null || _a === void 0 ? void 0 : _a.find(function (contest) { return contest.contest_id === contestId; })) === null || _b === void 0 ? void 0 : _b.choices.find(function (choice) { return answerIndex === choice.id; });
}; };
export var selectBallotSelectionQuestion = function (electionId, contestId) { return function (state) { var _a; return (_a = state.ballotSelections[electionId]) === null || _a === void 0 ? void 0 : _a.find(function (contest) { return contest.contest_id === contestId; }); }; };
export var selectBallotSelectionByElectionId = function (electionId) { return function (state) {
    return state.ballotSelections[electionId];
}; };
export default ballotSelectionsSlice.reducer;
